import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ApiService } from 'core/api/api.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { NotificationService } from 'core/notification/notification.service';
import { PaymentAddress } from '../../../crm/contacts/entities/contact-address.class';
import { Carrier } from '../entities/carrier.entity';
import { CustomerPaymentDetails } from '../entities/customer-payment-details.class';
import { PaymentDetails } from '../entities/payment-details.class';
import { QuoteAttributes } from '../entities/quote-attributes.interface';
import { PaymentSummary } from '../entities/payment-summary.class';
import { PaymentTerm } from '../entities/payment-term.class';
import { ShortContact } from '../entities/short-contact.class';
import { TaxDetails } from '../entities/tax-details.class';
import { ChasePaymentService } from './chase-payment.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentDetailsService {
  usCountryLabel = 'United States';
  paymentSummary$ = new Subject<PaymentSummary>();
  customerCountryCode$ = new Subject<string>();
  customerCurrencySign$ = new Subject<string>();
  shipToCompleteChecked$ = new Subject<string>();
  paymentSummaryError$ = new Subject<boolean>();
  contactEmail$ = new BehaviorSubject<{ newEmail: string, userChanged?: boolean }>({ newEmail: null });
  savedContact$ = new Subject<PaymentDetails['contact']>();
  cachedTemplateValues: any;
  cachedUserAttributes: any;
  userAttributes$ = new Subject<any>();

  callsDone = {
    shippingAddresses: false,
    billingAddresses: false
  };

  urls = {
    quoteAttributesList: '/api/payment/userattributes',
    templateValues: '/api/payment/templatevalues',
    defaultBillingAddress: '/api/payment/defaultBillingAddresses',
    customerContactsUrl: '/api/payment/customercontacts',
    leadContactsUrl: '/api/payment/leadcontacts',
    customerPaymentDetailsUrl: '/api/payment/customerdetails',
    carriersListUrl: '/api/payment/carriers',
    defaultCarriersListUrl: '/api/payment/defaultCarriers',
    customerBillingAddressesUrl: '/api/payment/billingaddresses',
    customerShippingAddressesUrl: '/api/payment/shippingaddress',
    paymentTermsUrl: '/api/payment/terms',
    paymentSummaryUrl: '/api/payment/summary',
    paymentDetailsUrl: '/api/payment/get',
    savePaymentDetailsUrl: '/api/payment/create/edit',
    chaseTokenUrl: '/api/payment/creditCardToken',
    processCCPaymentUrl: '/api/payment/creditCardPayment',
  };
  private currentHasFreight = false;

  constructor(
    private apiService: ApiService,
    private chasePaymentService: ChasePaymentService,
    private notificationService: NotificationService,
    private appStateService: AppStateService
  ) {
    this.appStateService.offlineStateToggle$.subscribe(() => {
      this.cachedUserAttributes = null;
    });
  }

  /**
   * Gets customer contacts
   * @param customerId - The customer id
   * @param callback - The callback
   */
  getCustomerContacts(customerId, callback: (contacts: ShortContact[]) => any) {
    this.apiService.get<ShortContact[]>(`${this.urls.customerContactsUrl}/${customerId}`)
      .subscribe((result: ShortContact[]) => callback(result));
  }

  getUserAttributes(callback: (param) => any) {
    if (this.cachedUserAttributes) {
      this.userAttributes$.next(this.cachedUserAttributes);
      callback(this.cachedUserAttributes);
    } else {
      this.apiService.get(this.urls.quoteAttributesList).subscribe((result) => {
        this.cachedUserAttributes = result;
        this.userAttributes$.next(result);
        callback(result);
      });
    }
  }

  /**
   * Saves quote attributes
   * @param attributes - The quote attributes to save
   */
  saveQuoteAttributes(attributes: QuoteAttributes) {
    this.cachedUserAttributes = attributes;
    this.userAttributes$.next(attributes);
  }

  getStaticValues(callback) {
    if (this.cachedTemplateValues) {
      callback(this.cachedTemplateValues);
    } else {
      this.apiService.get(this.urls.templateValues).subscribe(returnValues => {
        this.cachedTemplateValues = returnValues;
        callback(returnValues);
      });
    }
  }

  /**
   * Gets lead contacts
   * @param leadId - The lead id
   * @param callback - The callback
   */
  getLeadContacts(leadId, callback: (contacts: ShortContact[]) => any) {
    this.apiService.get<ShortContact[]>(`${this.urls.leadContactsUrl}/${leadId}`)
      .subscribe((result: ShortContact[]) => callback(result));
  }

  /**
   * Gets all customer-related payment details (not needed for lead):
   * Default Shipping Address
   * Area
   * Default Billing Address
   * Default Payment Derms
   * @param customerId - The customer id
   * @param callback - The callback
   */
  getCustomerPaymentDetails(customerId, callback: (paymentDetails: CustomerPaymentDetails) => any) {
    if (!customerId) {
      return;
    }
    this.apiService.get(`${this.urls.customerPaymentDetailsUrl}/${customerId}/`)
      .subscribe((result: CustomerPaymentDetails) => {
        const { lnCountryName, currencySign } = result;
        this.customerCountryCode$.next(lnCountryName);
        this.customerCurrencySign$.next(currencySign);
        callback(result);
      });
  }

  /**
   * Gets a list of all available carriers
   * @param callback - The callback
   */
  getCarriersList(quoteId, callback: (carriers: Carrier[]) => any) {
    this.apiService.get<Carrier[]>(`${this.urls.carriersListUrl}/${quoteId}`)
      .subscribe((result: Carrier[]) => callback(result));
  }

  /**
   * Gets a list of all available carriers including default
   * @param as carrierCode
   * @param as callback
   */
  getDefaultCarriersList(carrierCode, callback: (carriers: Carrier[]) => any, customerId?) {
    this.apiService.get<Carrier[]>(`${this.urls.defaultCarriersListUrl}/${carrierCode}?customerId=${customerId}`)
      .subscribe((result: Carrier[]) => callback(result));
  }

  /**
   * Gets the Customer Billing Address
   * Not applicable for Lead
   * @param customerId - The customer id
   * @param callback - The callback
   */
  getCustomerBillingAddresses(customerId: string, callback: (billingAddresses: PaymentAddress[]) => any) {
    if (!customerId) {
      return;
    }
    this.apiService.get<PaymentAddress[]>(`${this.urls.customerBillingAddressesUrl}/${customerId}`)
      .subscribe((result: PaymentAddress[]) => {
        result.forEach((addr: PaymentAddress) => {
          if (addr.addressline1) {
            addr.addressline1 = addr.addressline1.trim();
          }
          if (addr.addressline2) {
            addr.addressline2 = addr.addressline2.trim();
          }
        });
        callback(result);
      });
  }

  /**
   * Gets the Customer Shipping Address
   * Not applicable for Lead
   * @param addressId - The shipping address Id
   * @param callback - The callback
   */
  getCustomerShippingAddress(addressId: number, callback: (shippingAddress: PaymentAddress) => any) {
    this.apiService.get<PaymentAddress>(`${this.urls.customerShippingAddressesUrl}/${addressId}`)
      .subscribe((result: PaymentAddress) => callback(result));
  }

  /**
   * Gets the list of all available payment terms
   * @param callback - The callback
   */
  getPaymentTermsList(callback: (paymentTerms: PaymentTerm[]) => any) {
    this.apiService.get<PaymentTerm[]>(this.urls.paymentTermsUrl)
      .subscribe((result: PaymentTerm[]) => callback(result));
  }

  /**
   * Calculates the payment summary:
   * SubTotal, Tax, FreightCost, TotalWeight, GrandTotal
   * @param quoteId - The ID of the quote
   * @param taxDetails - Tax rate and freight costs
   */
  calculatePaymentSummary(quoteId: string, callback?: (...args: any[]) => any, taxDetails?: TaxDetails) {
    this.apiService.post(this.urls.paymentSummaryUrl, { id: quoteId, ...taxDetails })
      .subscribe((result: PaymentSummary) => this.processSummary(result, true, callback), err => this.handlePaymentSummaryError(err));
  }

  /**
   * Gets the payment summary:
   * SubTotal, Tax, FreightCost, TotalWeight, GrandTotal
   * @param quoteId - The ID of the quote
   */
  getPaymentSummary(quoteId: string, callback?: (...args: any[]) => any) {
    this.apiService.get(`${this.urls.paymentSummaryUrl}/${quoteId}`)
      .subscribe((result: PaymentSummary) => this.processSummary(result, false, callback));
  }

  resetCurrentFreightState() {
    this.currentHasFreight = false;
  }

  /**
   * Processes payment summary
   * @param summary - The payment summary prices
   */
  processSummary(summary: PaymentSummary, checkCarriers, callback?: (...args: any[]) => any) {
    if (summary.subTotal === null) {
      summary.subTotal = 0;
    }

    if (summary.freightCost === null) {
      summary.freightCost = 0;
    }

    if (summary.tax === null) {
      summary.tax = 0;
    }

    if (summary.totalWeight === null) {
      summary.totalWeight = 0;
    }

    if (summary.grandTotal === null) {
      summary.grandTotal = 0;
    }

    // check if isFreight is different than the last time, and set refreshCarriers
    if (checkCarriers && summary.isFreight !== this.currentHasFreight) {
      summary.shouldRefreshCarriers = true;
    }

    if (summary.carrierMessage) {
      this.notificationService.showNotification(summary.carrierMessage, NotificationType.Success);
    }

    if (callback) {
      callback();
    }

    this.currentHasFreight = summary.isFreight;
    this.paymentSummary$.next(summary);
  }

  /**
   * Handles payment summary calculation errors received from backend
   * @param err - The custom error sent by the backend
   */
  private handlePaymentSummaryError(err) {
    // make sure the error is sent from backend
    if (err.error) {
      this.paymentSummaryError$.next(true);
    }
  }

  /**
   * Gets the payment details
   * @param paymentId - The ID of the payment
   * @param callback - The callback
   */
  getPaymentDetails(paymentId: string, callback: (paymentDetails: PaymentDetails) => any) {
    this.apiService.get<PaymentDetails>(`${this.urls.paymentDetailsUrl}/${paymentId}`)
      .subscribe((result: PaymentDetails) => callback(result));
  }

  /**
   * Gets the payment details
   * @param quoteId - The ID of the quote
   * @param callback - The callback
   */
  savePaymentDetails(paymentDetails: PaymentDetails) {
    return this.apiService.post(this.urls.savePaymentDetailsUrl, paymentDetails);
  }

  /**
   * Gets the Chase payment token
   * @param quoteId - The quote id
   * @param currentBillingAddress - The currently selected billing address
   */
  getTokenForChase(quoteId, currentBillingAddress) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ...billingAddressWithoutId } = currentBillingAddress;
    return this.apiService.postBlob(this.urls.chaseTokenUrl, {
      quoteId,
      billingAddress: billingAddressWithoutId
    });
  }

  startCreditCardPayment(quoteId, paymentToken, successCallback, cancelCallback, errorCallback) {
    this.chasePaymentService.handlePayment(
      quoteId,
      paymentToken,
      response => {
        this.processCCPayment(response)
          .subscribe(
            updateResponse => successCallback(updateResponse),
            () => this.processFailedPayment(errorCallback));
      },
      canceledResponse => this.processCCPayment(canceledResponse)
        .subscribe(
          () => cancelCallback(),
          () => this.processFailedPayment(errorCallback)
        ),
      errResponse => this.processCCPayment(errResponse)
        .subscribe(
          () => {
            // This is intentional
          },
          () => this.processFailedPayment(errorCallback))
    );
  }

  private processFailedPayment(errorCallback) {
    this.chasePaymentService.closeCREPayment();
    errorCallback();
  }

  /**
   * Gets default CC/PO addresses
   */
  getDoNotMailAddresses() {
    return this.apiService.get<PaymentDetails>(this.urls.defaultBillingAddress);
  }

  /**
   * Gets do not mail address
   */
  getDoNotMailAddress(defaultAddr) {
    const doNotMailAddress = new PaymentAddress();
    doNotMailAddress.id = defaultAddr.id;
    doNotMailAddress.name = defaultAddr.name;
    doNotMailAddress.addressline1 = defaultAddr.addressline1;
    doNotMailAddress.city = defaultAddr.city;
    doNotMailAddress.state = defaultAddr.state;
    doNotMailAddress.zip = defaultAddr.zip;
    doNotMailAddress.country = defaultAddr.country;
    return doNotMailAddress;
  }

  private processCCPayment(paymentObject) {
    const reqObj = {
      QuoteId: paymentObject.QuoteId,
      Uid: paymentObject.UID,
      Status: paymentObject.Status,
      ErrorCode: paymentObject.ErrorCode
    };
    return this.apiService.post(this.urls.processCCPaymentUrl, reqObj);
  }

  public getUnitedStatesCountryId(countryList) {
    return (countryList.find(country => country.value === this.usCountryLabel)).key;
  }

  /**
   * Resets the flags used to check if calls are done
   */
  resetCallsDoneFlag() {
    this.callsDone = {
      shippingAddresses: false,
      billingAddresses: false
    };
  }
}
